import { lazy, Suspense, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnalyticsLayout } from "../components/layout/AnalyticsLayout";
import { KpiStrip } from "../components/analytics/KpiStrip";
import { Leaderboard } from "../components/analytics/Leaderboard";
import { FilterBar, EMPTY_FILTERS, type ActivityFilters } from "../components/analytics/FilterBar";
import { ActivityTable } from "../components/analytics/ActivityTable";
import { Spinner } from "../components/ui/Spinner";
import { useActivityList, useActivityOverview, useActivityTimeseries } from "../hooks/useActivity";
import { exportCsvHref } from "../lib/api/activity";
import { resolveRange, type RangeKey } from "../lib/range";
import type { ActivityFilterParams, InvestorCard } from "../lib/api/types";

// Recharts is heavy and admin-only — keep it out of the investor bundle.
const EngagementTrendChart = lazy(() =>
  import("../components/analytics/EngagementTrendChart").then((m) => ({
    default: m.EngagementTrendChart,
  })),
);

const PAGE_SIZE = 50;

function ChartFallback() {
  return (
    <div className="flex h-[420px] items-center justify-center rounded-lg border border-surface-border bg-surface">
      <Spinner className="h-8 w-8 text-brand-600" label="Loading chart" />
    </div>
  );
}

function toParams(filters: ActivityFilters, page: number): ActivityFilterParams {
  return {
    userId: filters.userId || undefined,
    action: filters.action || undefined,
    from: filters.from ? `${filters.from}T00:00:00` : undefined,
    to: filters.to ? `${filters.to}T23:59:59` : undefined,
    page,
    pageSize: PAGE_SIZE,
  };
}

export function AnalyticsPage() {
  const navigate = useNavigate();

  // Range drives the chart, KPI deltas, and leaderboard sparklines together.
  const [now] = useState(() => Date.now());
  const [range, setRange] = useState<RangeKey>("30d");
  const window = useMemo(() => resolveRange(range, now), [range, now]);

  const overview = useActivityOverview();

  // Stats feed: daily (or weekly for "all") non-cumulative over the previous +
  // current window, so KPI deltas and leaderboard sparklines are derivable.
  const stats = useActivityTimeseries({
    from: window.prevFrom,
    to: window.to,
    granularity: window.days != null ? "day" : "week",
    cumulative: false,
  });

  // Activity table (unchanged) keeps its own filters + pagination.
  const [filters, setFilters] = useState<ActivityFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const tableParams = useMemo(() => toParams(filters, page), [filters, page]);
  const list = useActivityList(tableParams);
  const onFilterChange = (next: ActivityFilters) => {
    setFilters(next);
    setPage(1);
  };

  const enabled = overview.data?.enabled !== false;
  const investors: InvestorCard[] =
    overview.data && overview.data.enabled ? overview.data.investors : [];
  const activeInvestors =
    overview.data && overview.data.enabled ? overview.data.activeInvestors : 0;

  const colorById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const inv of stats.data?.investors ?? []) map[inv.id] = inv.color;
    return map;
  }, [stats.data]);

  if (overview.isLoading) {
    return (
      <AnalyticsLayout>
        <div className="flex h-64 items-center justify-center">
          <Spinner className="h-8 w-8 text-brand-600" label="Loading analytics" />
        </div>
      </AnalyticsLayout>
    );
  }

  if (!enabled) {
    return (
      <AnalyticsLayout>
        <Header />
        <div className="mt-6 rounded-lg border border-dashed border-surface-border bg-surface p-8 text-center text-body text-text-muted">
          Activity tracking is currently disabled. Enable it to start recording opens and downloads.
        </div>
      </AnalyticsLayout>
    );
  }

  return (
    <AnalyticsLayout>
      <Header />

      <div className="mt-6 flex flex-col gap-6">
        <KpiStrip stats={stats.data} window={window} activeInvestors={activeInvestors} now={now} />

        <Suspense fallback={<ChartFallback />}>
          <EngagementTrendChart window={window} range={range} onRangeChange={setRange} />
        </Suspense>

        {investors.length > 0 ? (
          <section className="flex flex-col gap-3">
            <h2 className="text-body font-semibold text-text">Investor leaderboard</h2>
            <Leaderboard
              investors={investors}
              colorById={colorById}
              stats={stats.data}
              window={window}
              onOpen={(userId) => navigate(`/analytics/users/${userId}`)}
            />
          </section>
        ) : null}

        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-body font-semibold text-text">Activity</h2>
            <a
              href={exportCsvHref(tableParams)}
              className="inline-flex h-9 items-center rounded-md border border-surface-border bg-surface px-3 text-body font-medium text-text hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              Export CSV
            </a>
          </div>

          <FilterBar investors={investors} value={filters} onChange={onFilterChange} />

          <ActivityTable
            rows={list.data?.rows ?? []}
            total={list.data?.total ?? 0}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            onSelectUser={(userId) => navigate(`/analytics/users/${userId}`)}
            onSelectFile={(fileId) => navigate(`/analytics/files/${fileId}`)}
            isLoading={list.isLoading}
          />
        </section>
      </div>
    </AnalyticsLayout>
  );
}

function Header() {
  return (
    <div>
      <h1 className="text-title font-semibold text-text">Analytics</h1>
      <p className="mt-1 text-body text-text-muted">Investor engagement over time.</p>
    </div>
  );
}
